import { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
    selectCurrentWarehouseStatus,
    selectWasWarehouseLoadDialogOpenedAutomatically
} from 'app/store/healthcheck';
import { doLoadUserSessionWarehouses, selectUserSessionWarehousesList } from 'app/store/snowflake';
import { useDispatch, useRunOnce, useSelector } from 'app/lib/hooks';
import { Dialog, ModalBody, ModalHeader, ModalTitle } from 'app/components/core/Modal';
import { WarehouseLoadChart } from './WarehouseLoadChart.jsx';
import { ManageQueriesCard } from './ManageQueriesCard.jsx';
import { ChangeWarehouseCard } from './ChangeWarehouseCard.jsx';
import { WarehouseSelector } from './WarehouseSelector.jsx';
import { StateTag } from './StateTag.jsx';
import * as styles from './WarehouseLoadDialog.css';
import { NameTag } from './NameTag.jsx';
import { QueryList } from './QueryList.jsx';

const cx = classnames.bind(styles);

const Cards = {
    QUERY_LIST: 'QUERY_LIST',
    WAREHOUSE_LIST: 'WAREHOUSE_LIST'
};

export function WarehouseLoadDialog({ onClose, ...props }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('components', { keyPrefix: 'WarehouseLoadDialog' });
    const wasOpenedAutomatically = useSelector(selectWasWarehouseLoadDialogOpenedAutomatically);

    const wh = useSelector(selectCurrentWarehouseStatus);
    const state = wh.isOverloaded ? 'OVERLOADED' : wh?.state;

    const warehouses = useSelector(selectUserSessionWarehousesList);

    const [selectedCard, setSelectedCard] = useState(null);

    const log = useCallback(
        (msg) => {
            if (wasOpenedAutomatically && window.DD_RUM) {
                window.DD_RUM.addAction(`[Warehouse Load Dialog] ${msg}`);
            }
        },
        [wasOpenedAutomatically]
    );

    useRunOnce(() => {
        log(`Opened automatically due to overload of ${wh.name}.`);
    });

    useEffect(() => {
        dispatch(doLoadUserSessionWarehouses());
    }, [dispatch]);

    function gotoChartView() {
        setSelectedCard(null);
    }

    function handleClickQueryList() {
        log('Selected "Manage Queries" action.');
        if (selectedCard === Cards.QUERY_LIST) {
            setSelectedCard(null);
        }
        if (selectedCard === null) {
            setSelectedCard(Cards.QUERY_LIST);
        }
    }

    function handleClickWarehouseList() {
        log('Selected "Change Warehouse" action.');
        if (selectedCard === Cards.WAREHOUSE_LIST) {
            setSelectedCard(null);
        }
        if (selectedCard === null) {
            setSelectedCard(Cards.WAREHOUSE_LIST);
        }
    }

    return (
        <Dialog
            id="warehouse-load-dialog"
            onClose={onClose}
            width={1000}
            height={600}
            isResizable
            {...props}>
            <ModalHeader icon="warehouse">
                <ModalTitle className={cx('title')}>
                    {t('warehouse')}
                    <NameTag value={wh.name} />
                    <hr aria-orientation="vertical" />
                    {t('status')}
                    <StateTag value={state} />
                </ModalTitle>
            </ModalHeader>
            <ModalBody className={cx('body')}>
                {selectedCard === null && (
                    <WarehouseLoadChart
                        warehouse={wh.name}
                        data={wh.samples}
                        isOverloaded={wh.isOverloaded}
                    />
                )}
                {selectedCard === Cards.QUERY_LIST && (
                    <QueryList onCancel={gotoChartView} log={log} />
                )}
                {selectedCard === Cards.WAREHOUSE_LIST && (
                    <WarehouseSelector
                        current={wh.name}
                        options={warehouses}
                        onCancel={gotoChartView}
                        log={log}
                    />
                )}
                <h2 className={cx('actions-header')}>{t('actions')}</h2>
                <div className={cx('actions')}>
                    <ManageQueriesCard
                        isActive={selectedCard === Cards.QUERY_LIST}
                        onClick={handleClickQueryList}
                    />
                    <ChangeWarehouseCard
                        count={warehouses?.length ?? 0}
                        isActive={selectedCard === Cards.WAREHOUSE_LIST}
                        onClick={handleClickWarehouseList}
                    />
                </div>
            </ModalBody>
        </Dialog>
    );
}
